import { FC, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Col, Row, Button } from 'react-bootstrap';
import PageTitle from 'components/PageTitle';
import { GeofenceOverrideRequestDto } from 'types/geofence-override';
import { useApiClient } from 'helpers/api/apiCoreNewClient';
import { GoogleMap, Circle, Marker } from '@react-google-maps/api';
import { success, error } from 'utils/notification';
import DomainsService from 'redux/domains/DomainsService';
import { MDIIcon } from 'pages/icons/MDIIcons';
import ConfirmRejectBadAddressModal from "./ConfirmRejectBadAddressModal";
import { faL } from '@fortawesome/free-solid-svg-icons';

const REFRESH_INTERVAL = 15000; // 15 seconds

const GeofenceOverrideDetail: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const client = useApiClient();
    const [request, setRequest] = useState<GeofenceOverrideRequestDto | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isFirstFetch, setIsFirstFetch] = useState(true);
    const [initialCenter, setInitialCenter] = useState({ lat: 0, lng: 0 });
    

    const fetchRequest = useCallback(async () => {
        if (hasError || !id) return; // Don't retry if we've already encountered an error or if id is undefined
        
        try {
            setIsLoading(true);
            const response = await DomainsService.getGeofenceOverrideByTaskId(client, id);
            if (!response) {
                throw new Error('Request not found');
            }

            // Map the response to GeofenceOverrideRequestDto
            const mappedResponse: GeofenceOverrideRequestDto = {
                btParcelId: response.btParcelId,
                stopOrder: response.stopOrder,
                id: response.parcelId,
                driverName: `${response.driver.firstName} ${response.driver.lastName}`,
                driverPhone: response.driverPhone || 'N/A',
                address: response.fullAddress || 'N/A',
                status: response.geofenceOverrideStatus === 'Requested' ? 'PENDING' :
                       response.geofenceOverrideStatus === 'UnderReview' ? 'UNDER_REVIEW' :
                       response.geofenceOverrideStatus === 'Approved' ? 'APPROVED' : 
                       response.geofenceOverrideStatus === 'RejectedDriverAtWrongLocation'? 'REJECTED_DRIVER_WRONG_LOCATION': 'REJECTED_BAD_ADDRESS',
                requestedAt: response.objectCreated,
                latitude: response.latitude,
                longitude: response.longitude,
                geofenceRadius: response.geofenceRadius || 100,
                notes: '',
                approvedBy: undefined,
                approvedAt: undefined,
                rejectionReason: undefined,
                requiredGeofenceApprovalRequestLatitude: response.requiredGeofenceApprovalRequestLatitude || undefined,
                requiredGeofenceApprovalRequestLongitude: response.requiredGeofenceApprovalRequestLongitude || undefined
            };
            setRequest(mappedResponse);
        } catch (err: any) {
            setHasError(true);
            if (err.message === 'Request not found') {
                error('Geofence override request not found');
            } else {
                error('Failed to fetch geofence override request');
            }
            navigate('/fleet-ops/geofence-overrides');
        } finally {
            setIsLoading(false);
        }
    }, [id, client, navigate, hasError]);

    useEffect(() => {
        // Initial fetch
        fetchRequest();

        // Set up periodic refresh
        const intervalId = setInterval(fetchRequest, REFRESH_INTERVAL);

        // Cleanup interval on component unmount
        return () => clearInterval(intervalId);
    }, [fetchRequest]);

    const handleApprove = async () => {
        try {
            setIsLoading(true);
            await DomainsService.approveGeofenceOverride(client, id!);
            success('Geofence override request approved successfully');
            // Update the request status locally
            if (request) {
                setRequest({
                    ...request,
                    status: 'APPROVED',
                    approvedAt: new Date().toISOString()
                });
            }
        } catch (err) {
            error('Failed to approve geofence override request');
        } finally {
            setIsLoading(false);
        }
    };

    const handleReview = async () => {
        try {
            setIsLoading(true);
            await DomainsService.reviewGeofenceOverride(client, id!);
            success('Geofence override request marked for review');
            // Update the request status locally
            if (request) {
                setRequest({
                    ...request,
                    status: 'UNDER_REVIEW'
                });
            }
        } catch (err) {
            error('Failed to mark geofence override request for review');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectedDriverInWrongLocation = async () => {
        try {
            setIsLoading(true);
            await DomainsService.rejectGeofenceOverrideDriverWrongLocation(client, id!);
            success('Geofence override request marked rejected due to driver at wrong location');
            // Update the request status locally
            if (request) {
                setRequest({
                    ...request,
                    status: 'REJECTED_DRIVER_WRONG_LOCATION'
                });
            }
        } catch (err) {
            error('Failed to mark geofence override request rejected for the driver being at the wrong location');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectedBadAddress = async () => {
        try {
            setIsLoading(true);
            setShowConfirmationBadAddressModal(false)
            await DomainsService.rejectGeofenceOverrideBadAddress(client, id!);
            success('Geofence override request marked reject due to a bad address');
            // Update the request status locally
            if (request) {
                setRequest({
                    ...request,
                    status: 'REJECTED_BAD_ADDRESS'
                });
            }
        } catch (err) {
            error('Failed to mark geofence override request rejected for a bad address');
        } finally {
            setIsLoading(false);
        }
    };

    // Display confirmation dialog if user selects Bad Address
    const [showConfirmationBadAddressModal, setShowConfirmationBadAddressModal] = useState(false);

    const showConfirmBadAddress = async(show: boolean) => {
        setShowConfirmationBadAddressModal(show)
    };

    const mapContainerStyle = {
        width: '100%',
        height: '400px'
    };

    if (isLoading && isFirstFetch) {
        return <div>Loading...</div>;
    }

    if (!request) {
        return null;
    }
    
    const center = {
        lat: request?.latitude || 0,
        lng: request?.longitude || 0
    };

    if (isFirstFetch && request) {
        setIsFirstFetch(false);
        setInitialCenter(center);
    }

    return (
        <>
            <PageTitle
                breadCrumbItems={[
                    { label: 'Customer Services', path: '/fleet-ops' },
                    { label: 'Geofence Overrides', path: '/fleet-ops/geofence-overrides' },
                    { label: 'Request Details', path: `/fleet-ops/geofence-overrides/${id}`, active: true },
                ]}
                title={'Geofence Override Request Details'}
            />

            <Card>
                <Card.Body>
                    <Row className="mb-3">
                        <Col>
                            <Button
                                variant="secondary"
                                onClick={() => navigate('/fleet-ops/geofence-overrides')}
                            >
                                <MDIIcon icon="arrow-left" className="me-1" />
                                Back to List
                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <h4>Request Information</h4>
                            <p><strong>Driver Name:</strong> {request?.driverName}</p>
                            <p><strong>BT Parcel ID:</strong> {request?.btParcelId}</p>
                            <p><strong>Stop Order:</strong> {request?.stopOrder || 'N/A'}</p>
                            <p>
                                <strong>Driver Phone:</strong> {request?.driverPhone}
                                {request?.driverPhone !== 'N/A' && (
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2"
                                        onClick={() => {
                                            navigator.clipboard.writeText(request?.driverPhone || '');
                                            success('Phone number copied to clipboard');
                                        }}
                                    >
                                        <MDIIcon icon="content-copy" size={18} />
                                    </Button>
                                )}
                            </p>
                            <p>
                                <strong>Address:</strong> {request?.address}
                                {request?.address !== 'N/A' && (
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2"
                                        onClick={() => {
                                            navigator.clipboard.writeText(request?.address || '');
                                            success('Address copied to clipboard');
                                        }}
                                    >
                                        <MDIIcon icon="content-copy" size={18} />
                                    </Button>
                                )}
                            </p>
                            <p><strong>Status:</strong> 
                                <span className={`badge bg-${request?.status === 'PENDING' ? 'warning' : request?.status === 'APPROVED' ? 'success' : 'danger'} ms-2`}>
                                    {request?.status}
                                </span>
                            </p>
                            <p><strong>Requested At:</strong> {new Date(request?.requestedAt + 'Z').toLocaleString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZoneName: 'short'
                            })}</p>
                            {request?.approvedBy && (
                                <p><strong>Approved By:</strong> {request.approvedBy}</p>
                            )}
                            {request?.approvedAt && (
                                <p><strong>Approved At:</strong> {new Date(request.approvedAt + 'Z').toLocaleString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    timeZoneName: 'short'
                                })}</p>
                            )}
                            {request?.rejectionReason && (
                                <p><strong>Rejection Reason:</strong> {request.rejectionReason}</p>
                            )}
                        </Col>
                        <Col md={6}>
                            <GoogleMap
                                mapContainerStyle={mapContainerStyle}
                                center={initialCenter}
                                zoom={15}
                            >
                                <Marker 
                                    position={center}
                                    icon={{
                                        url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
                                    }}
                                />
                                {request?.requiredGeofenceApprovalRequestLatitude && request?.requiredGeofenceApprovalRequestLongitude && (
                                    <Marker 
                                        position={{
                                            lat: request.requiredGeofenceApprovalRequestLatitude,
                                            lng: request.requiredGeofenceApprovalRequestLongitude
                                        }}
                                        icon={{
                                            url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                                        }}
                                    />
                                )}
                                <Circle
                                    center={center}
                                    radius={request?.geofenceRadius}
                                    options={{
                                        strokeColor: '#FF0000',
                                        strokeOpacity: 0.8,
                                        strokeWeight: 2,
                                        fillColor: '#FF0000',
                                        fillOpacity: 0.35,
                                    }}
                                />
                            </GoogleMap>
                        </Col>
                    </Row>

                    {request?.status === 'PENDING' && (
                        <Row className="mt-4">
                            <Col>
                                <Button
                                    variant="success"
                                    onClick={handleApprove}
                                    disabled={isLoading}
                                    className="me-2"
                                >
                                    Approve Request
                                </Button>
                                <Button
                                    variant="warning"
                                    onClick={handleReview}
                                    disabled={isLoading}
                                    className="me-2"
                                >
                                    Mark Under Review
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleRejectedDriverInWrongLocation}
                                    disabled={isLoading}
                                    className="me-2"
                                >
                                    Mark Rejected Driver in Wrong Location
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => showConfirmBadAddress(true)}
                                    disabled={isLoading}
                                >
                                    Mark Rejected Bad Address
                                </Button>
                            </Col>
                        </Row>
                    )}
                    {request?.status === 'UNDER_REVIEW' && (
                        <Row className="mt-4">
                            <Col>
                                <Button
                                    variant="success"
                                    onClick={handleApprove}
                                    disabled={isLoading}
                                    className="me-2"
                                >
                                    Approve Request
                                </Button>
                                 <Button
                                    variant="danger"
                                    onClick={handleRejectedDriverInWrongLocation}
                                    disabled={isLoading}
                                    className="me-2"
                                >
                                    Mark Rejected Driver in Wrong Location
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => showConfirmBadAddress(true)}
                                    disabled={isLoading}
                                >
                                    Mark Rejected Bad Address
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            <ConfirmRejectBadAddressModal
                show={showConfirmationBadAddressModal}
                onHide={() => setShowConfirmationBadAddressModal(false)}
                onConfirm={
                    handleRejectedBadAddress
                }></ConfirmRejectBadAddressModal>
        </>
    );
};

export default GeofenceOverrideDetail; 